import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Toggle } from '../components/ui/Toggle';
import { Badge } from '../components/ui/Badge';
import { Save, Building2, Users, Bell, DollarSign, FileText, BarChart3, Plug, Image, Palette, Mail, Shield, Upload, Database } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
type SettingsSection = 'company' | 'buyer-experience' | 'users' | 'notifications' | 'mortgage' | 'pricing' | 'pdf' | 'integrations' | 'analytics' | 'content';
export function SettingsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    builderId
  } = useParams<{
    builderId: string;
  }>();
  // Get the base path for navigation
  const basePath = builderId ? `/${builderId}` : '';
  const getActiveSection = (): SettingsSection => {
    if (location.pathname.includes('/mortgage')) return 'mortgage';
    if (location.pathname.includes('/general')) return 'company';
    return 'company';
  };
  const [activeSection, setActiveSection] = useState<SettingsSection>(getActiveSection());
  const [hasChanges, setHasChanges] = useState(false);
  // Notification states
  const [notifications, setNotifications] = useState({
    newBuyer: true,
    tourCompletion: true,
    optionSelections: true,
    dailyDigest: true,
    weeklyReport: true
  });
  const sections = [{
    id: 'company' as const,
    label: 'Company Profile',
    icon: Building2
  }, {
    id: 'buyer-experience' as const,
    label: 'Buyer Experience',
    icon: Palette
  }, {
    id: 'users' as const,
    label: 'User Management',
    icon: Users
  }, {
    id: 'notifications' as const,
    label: 'Notifications',
    icon: Bell
  }, {
    id: 'mortgage' as const,
    label: 'Mortgage Calculator',
    icon: DollarSign
  }, {
    id: 'pricing' as const,
    label: 'Pricing & Display',
    icon: DollarSign
  }, {
    id: 'pdf' as const,
    label: 'PDF Summaries',
    icon: FileText
  }, {
    id: 'integrations' as const,
    label: 'Integrations',
    icon: Plug
  }, {
    id: 'analytics' as const,
    label: 'Analytics & Tracking',
    icon: BarChart3
  }, {
    id: 'content' as const,
    label: 'Content Management',
    icon: Image
  }];
  const handleSectionChange = (section: SettingsSection) => {
    setActiveSection(section);
    if (section === 'mortgage') {
      navigate(`${basePath}/settings/mortgage`);
    } else {
      navigate(`${basePath}/settings/general`);
    }
  };
  const handleSave = () => {
    console.log('Saving settings...');
    setHasChanges(false);
  };
  const handleNotificationToggle = (key: keyof typeof notifications) => {
    setNotifications(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
    setHasChanges(true);
  };
  const renderContent = () => {
    switch (activeSection) {
      case 'company':
        return <CompanyProfileSettings onChange={() => setHasChanges(true)} data-id="element-2259" />;
      case 'buyer-experience':
        return <BuyerExperienceSettings onChange={() => setHasChanges(true)} data-id="element-2260" />;
      case 'users':
        return <UserManagementSettings onChange={() => setHasChanges(true)} data-id="element-2261" />;
      case 'notifications':
        return <NotificationSettings notifications={notifications} onToggle={handleNotificationToggle} data-id="element-2262" />;
      case 'mortgage':
        return <MortgageCalculatorSettings onChange={() => setHasChanges(true)} data-id="element-2263" />;
      case 'pricing':
        return <PricingDisplaySettings onChange={() => setHasChanges(true)} data-id="element-2264" />;
      case 'pdf':
        return <PDFSummarySettings onChange={() => setHasChanges(true)} data-id="element-2265" />;
      case 'integrations':
        return <IntegrationSettings onChange={() => setHasChanges(true)} data-id="element-2266" />;
      case 'analytics':
        return <AnalyticsTrackingSettings onChange={() => setHasChanges(true)} data-id="element-2267" />;
      case 'content':
        return <ContentManagementSettings onChange={() => setHasChanges(true)} data-id="element-2268" />;
      default:
        return null;
    }
  };
  return <div className="flex gap-6 max-w-7xl mx-auto h-[calc(100vh-8rem)]" data-id="element-2269">
      {/* Sidebar Navigation */}
      <div className="w-64 flex-shrink-0" data-id="element-2270">
        <Card className="h-full overflow-y-auto" noPadding data-id="element-2271">
          <div className="p-4 border-b border-slate-200" data-id="element-2272">
            <h2 className="font-semibold text-slate-900" data-id="element-2273">Settings</h2>
          </div>
          <nav className="p-2" data-id="element-2274">
            {sections.map(section => {
            const Icon = section.icon;
            return <button key={section.id} onClick={() => handleSectionChange(section.id)} className={`
                    w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors
                    ${activeSection === section.id ? 'bg-primary-50 text-primary-700 font-medium' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}
                  `} data-id="element-2275">
                  <Icon className="h-4 w-4" data-id="element-2276" />
                  {section.label}
                </button>;
          })}
          </nav>
        </Card>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto" data-id="element-2277">
        <div className="space-y-6" data-id="element-2278">
          {/* Save Bar */}
          {hasChanges && <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between" data-id="element-2279">
              <p className="text-sm text-amber-800 font-medium" data-id="element-2280">
                You have unsaved changes
              </p>
              <Button onClick={handleSave} leftIcon={<Save className="h-4 w-4" data-id="element-2282" />} data-id="element-2281">
                Save Changes
              </Button>
            </div>}

          {renderContent()}
        </div>
      </div>
    </div>;
}
// Company Profile Settings - BRANDING FIRST
function CompanyProfileSettings({
  onChange
}: {
  onChange: () => void;
}) {
  return <div className="space-y-6" data-id="element-2283">
      <div data-id="element-2284">
        <h1 className="text-2xl font-bold text-slate-900" data-id="element-2285">Company Profile</h1>
        <p className="text-slate-500" data-id="element-2286">
          Manage your company information and branding
        </p>
      </div>

      {/* BRANDING SECTION FIRST */}
      <Card data-id="element-2287">
        <CardHeader data-id="element-2288">
          <CardTitle data-id="element-2289">Branding</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-2290">
          <div data-id="element-2291">
            <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-2292">
              Company Logo
            </label>
            <div className="flex items-center gap-4" data-id="element-2293">
              <div className="h-20 w-20 bg-slate-100 rounded-lg flex items-center justify-center border border-slate-200" data-id="element-2294">
                <Building2 className="h-8 w-8 text-slate-400" data-id="element-2295" />
              </div>
              <Button variant="outline" leftIcon={<Upload className="h-4 w-4" data-id="element-2297" />} data-id="element-2296">
                Upload Logo
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-2" data-id="element-2298">
              Recommended: PNG or SVG, 500x500px minimum
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4" data-id="element-2299">
            <div data-id="element-2300">
              <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-2301">
                Primary Brand Color
              </label>
              <div className="flex items-center gap-3" data-id="element-2302">
                <input type="color" defaultValue="#06b6d4" className="h-10 w-20 rounded border border-slate-300" onChange={onChange} data-id="element-2303" />
                <Input defaultValue="#06b6d4" onChange={onChange} data-id="element-2304" />
              </div>
            </div>
            <div data-id="element-2305">
              <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-2306">
                Secondary Brand Color
              </label>
              <div className="flex items-center gap-3" data-id="element-2307">
                <input type="color" defaultValue="#334155" className="h-10 w-20 rounded border border-slate-300" onChange={onChange} data-id="element-2308" />
                <Input defaultValue="#334155" onChange={onChange} data-id="element-2309" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* BASIC INFORMATION SECOND */}
      <Card data-id="element-2310">
        <CardHeader data-id="element-2311">
          <CardTitle data-id="element-2312">Basic Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-2313">
          <Input label="Company Name" defaultValue="Brookfield" onChange={onChange} data-id="element-2314" />
          <div className="grid grid-cols-2 gap-4" data-id="element-2315">
            <Input label="Phone Number" defaultValue="(555) 123-4567" onChange={onChange} data-id="element-2316" />
            <Input label="Email" defaultValue="info@brookfield.com" onChange={onChange} data-id="element-2317" />
          </div>
          <Input label="Address" defaultValue="123 Builder Lane, City, ST 12345" onChange={onChange} data-id="element-2318" />
          <div data-id="element-2319">
            <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-2320">
              Company Description
            </label>
            <textarea className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" rows={4} defaultValue="Building quality homes since 1995..." onChange={onChange} data-id="element-2321" />
          </div>
        </CardContent>
      </Card>

      <Card data-id="element-2322">
        <CardHeader data-id="element-2323">
          <CardTitle data-id="element-2324">Social Media</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-2325">
          <Input label="Facebook" placeholder="https://facebook.com/..." onChange={onChange} data-id="element-2326" />
          <Input label="Instagram" placeholder="https://instagram.com/..." onChange={onChange} data-id="element-2327" />
          <Input label="LinkedIn" placeholder="https://linkedin.com/..." onChange={onChange} data-id="element-2328" />
          <Input label="YouTube" placeholder="https://youtube.com/..." onChange={onChange} data-id="element-2329" />
        </CardContent>
      </Card>
    </div>;
}
// Buyer Experience Settings
function BuyerExperienceSettings({
  onChange
}: {
  onChange: () => void;
}) {
  return <div className="space-y-6" data-id="element-2330">
      <div data-id="element-2331">
        <h1 className="text-2xl font-bold text-slate-900" data-id="element-2332">Buyer Experience</h1>
        <p className="text-slate-500" data-id="element-2333">
          Customize the buyer journey and interface
        </p>
      </div>

      <Card data-id="element-2334">
        <CardHeader data-id="element-2335">
          <CardTitle data-id="element-2336">Welcome Message</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-2337">
          <div data-id="element-2338">
            <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-2339">
              Greeting Text
            </label>
            <textarea className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" rows={3} defaultValue="Welcome to your digital showhome experience! Explore our models and customize your dream home." onChange={onChange} data-id="element-2340" />
          </div>
        </CardContent>
      </Card>

      <Card data-id="element-2341">
        <CardHeader data-id="element-2342">
          <CardTitle data-id="element-2343">Tour Flow Preferences</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-2344">
          <div className="flex items-center justify-between" data-id="element-2345">
            <div data-id="element-2346">
              <p className="font-medium text-slate-900" data-id="element-2347">Guided Tour Mode</p>
              <p className="text-sm text-slate-500" data-id="element-2348">
                Walk buyers through a structured experience
              </p>
            </div>
            <Toggle defaultChecked onChange={onChange} data-id="element-2349" />
          </div>
          <div className="flex items-center justify-between" data-id="element-2350">
            <div data-id="element-2351">
              <p className="font-medium text-slate-900" data-id="element-2352">
                Allow Free Exploration
              </p>
              <p className="text-sm text-slate-500" data-id="element-2353">
                Let buyers navigate at their own pace
              </p>
            </div>
            <Toggle defaultChecked onChange={onChange} data-id="element-2354" />
          </div>
          <div className="flex items-center justify-between" data-id="element-2355">
            <div data-id="element-2356">
              <p className="font-medium text-slate-900" data-id="element-2357">
                Show Progress Indicator
              </p>
              <p className="text-sm text-slate-500" data-id="element-2358">
                Display completion percentage
              </p>
            </div>
            <Toggle defaultChecked onChange={onChange} data-id="element-2359" />
          </div>
        </CardContent>
      </Card>

      <Card data-id="element-2360">
        <CardHeader data-id="element-2361">
          <CardTitle data-id="element-2362">Lead Capture Form</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-2363">
          <div className="space-y-3" data-id="element-2364">
            {[{
            field: 'Name',
            required: true
          }, {
            field: 'Email',
            required: true
          }, {
            field: 'Phone',
            required: false
          }, {
            field: 'Move-in Timeline',
            required: false
          }, {
            field: 'Budget Range',
            required: false
          }].map(item => <div key={item.field} className="flex items-center justify-between" data-id="element-2365">
                <span className="text-sm text-slate-700" data-id="element-2366">{item.field}</span>
                <div className="flex items-center gap-4" data-id="element-2367">
                  <label className="flex items-center gap-2 text-sm text-slate-600" data-id="element-2368">
                    <input type="checkbox" defaultChecked={item.required} onChange={onChange} data-id="element-2369" />
                    Required
                  </label>
                  <Toggle defaultChecked onChange={onChange} data-id="element-2370" />
                </div>
              </div>)}
          </div>
        </CardContent>
      </Card>
    </div>;
}
// User Management Settings
function UserManagementSettings({
  onChange
}: {
  onChange: () => void;
}) {
  return <div className="space-y-6" data-id="element-2371">
      <div className="flex items-center justify-between" data-id="element-2372">
        <div data-id="element-2373">
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-2374">User Management</h1>
          <p className="text-slate-500" data-id="element-2375">Manage team members and permissions</p>
        </div>
        <Button leftIcon={<Users className="h-4 w-4" data-id="element-2377" />} data-id="element-2376">Invite User</Button>
      </div>

      <Card data-id="element-2378">
        <CardHeader data-id="element-2379">
          <CardTitle data-id="element-2380">Team Members</CardTitle>
        </CardHeader>
        <CardContent data-id="element-2381">
          <div className="space-y-3" data-id="element-2382">
            {[{
            name: 'John Doe',
            email: 'john@brookfield.com',
            role: 'Admin'
          }, {
            name: 'Sarah Johnson',
            email: 'sarah@brookfield.com',
            role: 'Editor'
          }, {
            name: 'Michael Chen',
            email: 'michael@brookfield.com',
            role: 'Viewer'
          }].map(user => <div key={user.email} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-id="element-2383">
                <div data-id="element-2384">
                  <p className="font-medium text-slate-900" data-id="element-2385">{user.name}</p>
                  <p className="text-sm text-slate-500" data-id="element-2386">{user.email}</p>
                </div>
                <div className="flex items-center gap-3" data-id="element-2387">
                  <Badge variant="neutral" data-id="element-2388">{user.role}</Badge>
                  <Button variant="ghost" size="sm" data-id="element-2389">
                    Edit
                  </Button>
                </div>
              </div>)}
          </div>
        </CardContent>
      </Card>

      <Card data-id="element-2390">
        <CardHeader data-id="element-2391">
          <CardTitle data-id="element-2392">Role Permissions</CardTitle>
        </CardHeader>
        <CardContent data-id="element-2393">
          <div className="space-y-4" data-id="element-2394">
            <div className="grid grid-cols-4 gap-4 text-sm font-medium text-slate-600 pb-2 border-b" data-id="element-2395">
              <div data-id="element-2396">Permission</div>
              <div className="text-center" data-id="element-2397">Admin</div>
              <div className="text-center" data-id="element-2398">Editor</div>
              <div className="text-center" data-id="element-2399">Viewer</div>
            </div>
            {['Manage Projects', 'Edit Options', 'View Analytics', 'Manage Users', 'Export Data'].map(permission => <div key={permission} className="grid grid-cols-4 gap-4 items-center" data-id="element-2400">
                <div className="text-sm text-slate-700" data-id="element-2401">{permission}</div>
                <div className="flex justify-center" data-id="element-2402">
                  <input type="checkbox" defaultChecked className="h-4 w-4" data-id="element-2403" />
                </div>
                <div className="flex justify-center" data-id="element-2404">
                  <input type="checkbox" defaultChecked={permission !== 'Manage Users'} className="h-4 w-4" data-id="element-2405" />
                </div>
                <div className="flex justify-center" data-id="element-2406">
                  <input type="checkbox" defaultChecked={permission === 'View Analytics'} className="h-4 w-4" data-id="element-2407" />
                </div>
              </div>)}
          </div>
        </CardContent>
      </Card>
    </div>;
}
// Notification Settings - WITH WORKING TOGGLES
function NotificationSettings({
  notifications,
  onToggle
}: {
  notifications: {
    newBuyer: boolean;
    tourCompletion: boolean;
    optionSelections: boolean;
    dailyDigest: boolean;
    weeklyReport: boolean;
  };
  onToggle: (key: keyof typeof notifications) => void;
}) {
  return <div className="space-y-6" data-id="element-2408">
      <div data-id="element-2409">
        <h1 className="text-2xl font-bold text-slate-900" data-id="element-2410">Notifications</h1>
        <p className="text-slate-500" data-id="element-2411">
          Configure email alerts and notifications
        </p>
      </div>

      <Card data-id="element-2412">
        <CardHeader data-id="element-2413">
          <CardTitle data-id="element-2414">Email Notifications</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-2415">
          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-2416">
            <div data-id="element-2417">
              <p className="font-medium text-slate-900" data-id="element-2418">
                New Buyer Registration
              </p>
              <p className="text-sm text-slate-500" data-id="element-2419">
                Get notified when someone starts a tour
              </p>
            </div>
            <Toggle checked={notifications.newBuyer} onChange={() => onToggle('newBuyer')} data-id="element-2420" />
          </div>
          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-2421">
            <div data-id="element-2422">
              <p className="font-medium text-slate-900" data-id="element-2423">Tour Completion</p>
              <p className="text-sm text-slate-500" data-id="element-2424">
                Alert when a buyer completes their tour
              </p>
            </div>
            <Toggle checked={notifications.tourCompletion} onChange={() => onToggle('tourCompletion')} data-id="element-2425" />
          </div>
          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-2426">
            <div data-id="element-2427">
              <p className="font-medium text-slate-900" data-id="element-2428">Option Selections</p>
              <p className="text-sm text-slate-500" data-id="element-2429">
                Notify when buyers select upgrades
              </p>
            </div>
            <Toggle checked={notifications.optionSelections} onChange={() => onToggle('optionSelections')} data-id="element-2430" />
          </div>
          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-2431">
            <div data-id="element-2432">
              <p className="font-medium text-slate-900" data-id="element-2433">Daily Digest</p>
              <p className="text-sm text-slate-500" data-id="element-2434">
                Summary of all activity sent daily at 9 AM
              </p>
            </div>
            <Toggle checked={notifications.dailyDigest} onChange={() => onToggle('dailyDigest')} data-id="element-2435" />
          </div>
          <div className="flex items-center justify-between py-3" data-id="element-2436">
            <div data-id="element-2437">
              <p className="font-medium text-slate-900" data-id="element-2438">Weekly Report</p>
              <p className="text-sm text-slate-500" data-id="element-2439">
                Comprehensive analytics every Monday
              </p>
            </div>
            <Toggle checked={notifications.weeklyReport} onChange={() => onToggle('weeklyReport')} data-id="element-2440" />
          </div>
        </CardContent>
      </Card>

      <Card data-id="element-2441">
        <CardHeader data-id="element-2442">
          <CardTitle data-id="element-2443">Notification Recipients</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-2444">
          <Input label="Primary Email" defaultValue="notifications@brookfield.com" data-id="element-2445" />
          <div data-id="element-2446">
            <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-2447">
              Additional Recipients
            </label>
            <div className="space-y-2" data-id="element-2448">
              <Input placeholder="email@example.com" data-id="element-2449" />
              <Button variant="outline" size="sm" data-id="element-2450">
                Add Recipient
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>;
}
// Mortgage Calculator Settings
function MortgageCalculatorSettings({
  onChange
}: {
  onChange: () => void;
}) {
  return <div className="space-y-6" data-id="element-2451">
      <div data-id="element-2452">
        <h1 className="text-2xl font-bold text-slate-900" data-id="element-2453">
          Mortgage Calculator
        </h1>
        <p className="text-slate-500" data-id="element-2454">
          Configure default mortgage calculation values
        </p>
      </div>

      <Card data-id="element-2455">
        <CardHeader data-id="element-2456">
          <CardTitle data-id="element-2457">Default Values</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6" data-id="element-2458">
          <p className="text-sm text-slate-600 bg-slate-50 p-4 rounded-lg" data-id="element-2459">
            These values will be used as defaults in the buyer's mortgage
            calculator. Buyers can override them in their own view.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2460">
            <Input label="Default Interest Rate (%)" defaultValue="6.5" type="number" step="0.1" onChange={onChange} data-id="element-2461" />
            <Input label="Down Payment (%)" defaultValue="20" type="number" step="1" onChange={onChange} data-id="element-2462" />
            <Input label="Loan Term (Years)" defaultValue="30" type="number" step="5" onChange={onChange} data-id="element-2463" />
            <Input label="Property Tax Rate (%)" defaultValue="1.2" type="number" step="0.1" onChange={onChange} data-id="element-2464" />
            <Input label="Home Insurance (Annual $)" defaultValue="1200" type="number" onChange={onChange} data-id="element-2465" />
            <Input label="HOA Fees (Monthly $)" defaultValue="150" type="number" onChange={onChange} data-id="element-2466" />
          </div>
        </CardContent>
      </Card>
    </div>;
}
// Pricing & Display Settings
function PricingDisplaySettings({
  onChange
}: {
  onChange: () => void;
}) {
  return <div className="space-y-6" data-id="element-2467">
      <div data-id="element-2468">
        <h1 className="text-2xl font-bold text-slate-900" data-id="element-2469">Pricing & Display</h1>
        <p className="text-slate-500" data-id="element-2470">Control how pricing is shown to buyers</p>
      </div>

      <Card data-id="element-2471">
        <CardHeader data-id="element-2472">
          <CardTitle data-id="element-2473">Price Visibility</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-2474">
          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-2475">
            <div data-id="element-2476">
              <p className="font-medium text-slate-900" data-id="element-2477">Show Base Prices</p>
              <p className="text-sm text-slate-500" data-id="element-2478">
                Display model starting prices
              </p>
            </div>
            <Toggle defaultChecked onChange={onChange} data-id="element-2479" />
          </div>
          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-2480">
            <div data-id="element-2481">
              <p className="font-medium text-slate-900" data-id="element-2482">Show Option Prices</p>
              <p className="text-sm text-slate-500" data-id="element-2483">Display upgrade pricing</p>
            </div>
            <Toggle defaultChecked onChange={onChange} data-id="element-2484" />
          </div>
          <div className="flex items-center justify-between py-3" data-id="element-2485">
            <div data-id="element-2486">
              <p className="font-medium text-slate-900" data-id="element-2487">Show Running Total</p>
              <p className="text-sm text-slate-500" data-id="element-2488">
                Display cumulative price as buyers select options
              </p>
            </div>
            <Toggle defaultChecked onChange={onChange} data-id="element-2489" />
          </div>
        </CardContent>
      </Card>

      <Card data-id="element-2490">
        <CardHeader data-id="element-2491">
          <CardTitle data-id="element-2492">Price Format</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-2493">
          <div data-id="element-2494">
            <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-2495">
              Currency Symbol
            </label>
            <select className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" onChange={onChange} data-id="element-2496">
              <option value="$" data-id="element-2497">$ (USD)</option>
              <option value="€" data-id="element-2498">€ (EUR)</option>
              <option value="£" data-id="element-2499">£ (GBP)</option>
              <option value="$" data-id="element-2500">$ (CAD)</option>
            </select>
          </div>
          <div className="flex items-center justify-between" data-id="element-2501">
            <div data-id="element-2502">
              <p className="font-medium text-slate-900" data-id="element-2503">
                Include Tax in Prices
              </p>
              <p className="text-sm text-slate-500" data-id="element-2504">
                Show tax-inclusive pricing
              </p>
            </div>
            <Toggle onChange={onChange} data-id="element-2505" />
          </div>
          <Input label="Tax Rate (%)" defaultValue="7.5" type="number" step="0.1" onChange={onChange} data-id="element-2506" />
        </CardContent>
      </Card>
    </div>;
}
// PDF Summary Settings
function PDFSummarySettings({
  onChange
}: {
  onChange: () => void;
}) {
  return <div className="space-y-6" data-id="element-2507">
      <div data-id="element-2508">
        <h1 className="text-2xl font-bold text-slate-900" data-id="element-2509">PDF Summaries</h1>
        <p className="text-slate-500" data-id="element-2510">Customize buyer PDF exports</p>
      </div>

      <Card data-id="element-2511">
        <CardHeader data-id="element-2512">
          <CardTitle data-id="element-2513">Branding</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-2514">
          <div className="flex items-center justify-between" data-id="element-2515">
            <div data-id="element-2516">
              <p className="font-medium text-slate-900" data-id="element-2517">Include Company Logo</p>
              <p className="text-sm text-slate-500" data-id="element-2518">Show logo on PDF header</p>
            </div>
            <Toggle defaultChecked onChange={onChange} data-id="element-2519" />
          </div>
          <div className="flex items-center justify-between" data-id="element-2520">
            <div data-id="element-2521">
              <p className="font-medium text-slate-900" data-id="element-2522">Include Contact Info</p>
              <p className="text-sm text-slate-500" data-id="element-2523">
                Add company details to footer
              </p>
            </div>
            <Toggle defaultChecked onChange={onChange} data-id="element-2524" />
          </div>
        </CardContent>
      </Card>

      <Card data-id="element-2525">
        <CardHeader data-id="element-2526">
          <CardTitle data-id="element-2527">Content Options</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-2528">
          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-2529">
            <span className="text-sm text-slate-700" data-id="element-2530">Model Details</span>
            <Toggle defaultChecked onChange={onChange} data-id="element-2531" />
          </div>
          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-2532">
            <span className="text-sm text-slate-700" data-id="element-2533">Selected Options</span>
            <Toggle defaultChecked onChange={onChange} data-id="element-2534" />
          </div>
          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-2535">
            <span className="text-sm text-slate-700" data-id="element-2536">Pricing Breakdown</span>
            <Toggle defaultChecked onChange={onChange} data-id="element-2537" />
          </div>
          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-2538">
            <span className="text-sm text-slate-700" data-id="element-2539">Mortgage Estimate</span>
            <Toggle defaultChecked onChange={onChange} data-id="element-2540" />
          </div>
          <div className="flex items-center justify-between py-3" data-id="element-2541">
            <span className="text-sm text-slate-700" data-id="element-2542">Option Images</span>
            <Toggle defaultChecked onChange={onChange} data-id="element-2543" />
          </div>
        </CardContent>
      </Card>

      <Card data-id="element-2544">
        <CardHeader data-id="element-2545">
          <CardTitle data-id="element-2546">Disclaimer Text</CardTitle>
        </CardHeader>
        <CardContent data-id="element-2547">
          <textarea className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" rows={4} defaultValue="Prices and specifications are subject to change without notice. Please contact us for the most current information." onChange={onChange} data-id="element-2548" />
        </CardContent>
      </Card>
    </div>;
}
// Integration Settings - REORDERED WITH ERP ADDED
function IntegrationSettings({
  onChange
}: {
  onChange: () => void;
}) {
  return <div className="space-y-6" data-id="element-2549">
      <div data-id="element-2550">
        <h1 className="text-2xl font-bold text-slate-900" data-id="element-2551">Integrations</h1>
        <p className="text-slate-500" data-id="element-2552">Connect with third-party services</p>
      </div>

      {/* CRM Integration */}
      <Card data-id="element-2553">
        <CardHeader data-id="element-2554">
          <CardTitle data-id="element-2555">CRM Integration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-2556">
          <div data-id="element-2557">
            <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-2558">
              Select CRM
            </label>
            <select className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" onChange={onChange} data-id="element-2559">
              <option value="" data-id="element-2560">None</option>
              <option value="salesforce" data-id="element-2561">Salesforce</option>
              <option value="hubspot" data-id="element-2562">HubSpot</option>
              <option value="zoho" data-id="element-2563">Zoho CRM</option>
              <option value="pipedrive" data-id="element-2564">Pipedrive</option>
            </select>
          </div>
          <Input label="API Key" type="password" placeholder="Enter API key" onChange={onChange} data-id="element-2565" />
          <Button variant="outline" data-id="element-2566">Test Connection</Button>
        </CardContent>
      </Card>

      {/* ERP Integration - NEW */}
      <Card data-id="element-2567">
        <CardHeader data-id="element-2568">
          <CardTitle data-id="element-2569">ERP Integration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-2570">
          <div data-id="element-2571">
            <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-2572">
              Select ERP System
            </label>
            <select className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" onChange={onChange} data-id="element-2573">
              <option value="" data-id="element-2574">None</option>
              <option value="sap" data-id="element-2575">SAP</option>
              <option value="oracle" data-id="element-2576">Oracle NetSuite</option>
              <option value="microsoft" data-id="element-2577">Microsoft Dynamics 365</option>
              <option value="sage" data-id="element-2578">Sage Intacct</option>
              <option value="acumatica" data-id="element-2579">Acumatica</option>
            </select>
          </div>
          <Input label="API Endpoint" placeholder="https://your-erp.com/api" onChange={onChange} data-id="element-2580" />
          <Input label="API Key" type="password" placeholder="Enter API key" onChange={onChange} data-id="element-2581" />
          <div className="flex items-center justify-between" data-id="element-2582">
            <div data-id="element-2583">
              <p className="font-medium text-slate-900" data-id="element-2584">
                Sync Orders Automatically
              </p>
              <p className="text-sm text-slate-500" data-id="element-2585">
                Push buyer selections to ERP
              </p>
            </div>
            <Toggle onChange={onChange} data-id="element-2586" />
          </div>
          <Button variant="outline" data-id="element-2587">Test Connection</Button>
        </CardContent>
      </Card>

      {/* Calendar Booking - MOVED UP */}
      <Card data-id="element-2588">
        <CardHeader data-id="element-2589">
          <CardTitle data-id="element-2590">Calendar Booking</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-2591">
          <div data-id="element-2592">
            <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-2593">
              Select Platform
            </label>
            <select className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" onChange={onChange} data-id="element-2594">
              <option value="" data-id="element-2595">None</option>
              <option value="calendly" data-id="element-2596">Calendly</option>
              <option value="acuity" data-id="element-2597">Acuity Scheduling</option>
              <option value="cal" data-id="element-2598">Cal.com</option>
            </select>
          </div>
          <Input label="Booking Link" placeholder="https://calendly.com/..." onChange={onChange} data-id="element-2599" />
        </CardContent>
      </Card>

      {/* Email Marketing */}
      <Card data-id="element-2600">
        <CardHeader data-id="element-2601">
          <CardTitle data-id="element-2602">Email Marketing</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-2603">
          <div data-id="element-2604">
            <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-2605">
              Select Platform
            </label>
            <select className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" onChange={onChange} data-id="element-2606">
              <option value="" data-id="element-2607">None</option>
              <option value="mailchimp" data-id="element-2608">Mailchimp</option>
              <option value="constant-contact" data-id="element-2609">Constant Contact</option>
              <option value="sendgrid" data-id="element-2610">SendGrid</option>
              <option value="klaviyo" data-id="element-2611">Klaviyo</option>
            </select>
          </div>
          <Input label="API Key" type="password" placeholder="Enter API key" onChange={onChange} data-id="element-2612" />
          <div className="flex items-center justify-between" data-id="element-2613">
            <div data-id="element-2614">
              <p className="font-medium text-slate-900" data-id="element-2615">Auto-sync New Buyers</p>
              <p className="text-sm text-slate-500" data-id="element-2616">
                Add to mailing list automatically
              </p>
            </div>
            <Toggle onChange={onChange} data-id="element-2617" />
          </div>
        </CardContent>
      </Card>

      {/* Webhooks */}
      <Card data-id="element-2618">
        <CardHeader data-id="element-2619">
          <CardTitle data-id="element-2620">Webhooks</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-2621">
          <p className="text-sm text-slate-600" data-id="element-2622">
            Send real-time data to your custom endpoints
          </p>
          <Input label="Webhook URL" placeholder="https://your-domain.com/webhook" onChange={onChange} data-id="element-2623" />
          <div data-id="element-2624">
            <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-2625">
              Trigger Events
            </label>
            <div className="space-y-2" data-id="element-2626">
              {['New Buyer', 'Tour Completed', 'Option Selected', 'PDF Downloaded'].map(event => <label key={event} className="flex items-center gap-2" data-id="element-2627">
                  <input type="checkbox" onChange={onChange} data-id="element-2628" />
                  <span className="text-sm text-slate-700" data-id="element-2629">{event}</span>
                </label>)}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>;
}
// Analytics & Tracking Settings
function AnalyticsTrackingSettings({
  onChange
}: {
  onChange: () => void;
}) {
  return <div className="space-y-6" data-id="element-2630">
      <div data-id="element-2631">
        <h1 className="text-2xl font-bold text-slate-900" data-id="element-2632">
          Analytics & Tracking
        </h1>
        <p className="text-slate-500" data-id="element-2633">
          Configure tracking and analytics integrations
        </p>
      </div>

      <Card data-id="element-2634">
        <CardHeader data-id="element-2635">
          <CardTitle data-id="element-2636">Google Analytics</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-2637">
          <div className="flex items-center justify-between mb-4" data-id="element-2638">
            <div data-id="element-2639">
              <p className="font-medium text-slate-900" data-id="element-2640">
                Enable Google Analytics
              </p>
              <p className="text-sm text-slate-500" data-id="element-2641">
                Track buyer behavior and conversions
              </p>
            </div>
            <Toggle onChange={onChange} data-id="element-2642" />
          </div>
          <Input label="Measurement ID" placeholder="G-XXXXXXXXXX" onChange={onChange} data-id="element-2643" />
        </CardContent>
      </Card>

      <Card data-id="element-2644">
        <CardHeader data-id="element-2645">
          <CardTitle data-id="element-2646">Meta Pixel</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-2647">
          <div className="flex items-center justify-between mb-4" data-id="element-2648">
            <div data-id="element-2649">
              <p className="font-medium text-slate-900" data-id="element-2650">Enable Meta Pixel</p>
              <p className="text-sm text-slate-500" data-id="element-2651">
                Track Facebook ad conversions
              </p>
            </div>
            <Toggle onChange={onChange} data-id="element-2652" />
          </div>
          <Input label="Pixel ID" placeholder="123456789012345" onChange={onChange} data-id="element-2653" />
        </CardContent>
      </Card>

      <Card data-id="element-2654">
        <CardHeader data-id="element-2655">
          <CardTitle data-id="element-2656">Custom Events</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-2657">
          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-2658">
            <span className="text-sm text-slate-700" data-id="element-2659">Track Model Views</span>
            <Toggle defaultChecked onChange={onChange} data-id="element-2660" />
          </div>
          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-2661">
            <span className="text-sm text-slate-700" data-id="element-2662">
              Track Option Selections
            </span>
            <Toggle defaultChecked onChange={onChange} data-id="element-2663" />
          </div>
          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-2664">
            <span className="text-sm text-slate-700" data-id="element-2665">Track PDF Downloads</span>
            <Toggle defaultChecked onChange={onChange} data-id="element-2666" />
          </div>
          <div className="flex items-center justify-between py-3" data-id="element-2667">
            <span className="text-sm text-slate-700" data-id="element-2668">
              Track Tour Completions
            </span>
            <Toggle defaultChecked onChange={onChange} data-id="element-2669" />
          </div>
        </CardContent>
      </Card>

      <Card data-id="element-2670">
        <CardHeader data-id="element-2671">
          <CardTitle data-id="element-2672">Data Privacy</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-2673">
          <Input label="Data Retention Period (days)" defaultValue="365" type="number" onChange={onChange} data-id="element-2674" />
          <div className="flex items-center justify-between" data-id="element-2675">
            <div data-id="element-2676">
              <p className="font-medium text-slate-900" data-id="element-2677">
                Cookie Consent Banner
              </p>
              <p className="text-sm text-slate-500" data-id="element-2678">
                Show GDPR/CCPA compliance notice
              </p>
            </div>
            <Toggle defaultChecked onChange={onChange} data-id="element-2679" />
          </div>
        </CardContent>
      </Card>
    </div>;
}
// Content Management Settings
function ContentManagementSettings({
  onChange
}: {
  onChange: () => void;
}) {
  return <div className="space-y-6" data-id="element-2680">
      <div data-id="element-2681">
        <h1 className="text-2xl font-bold text-slate-900" data-id="element-2682">
          Content Management
        </h1>
        <p className="text-slate-500" data-id="element-2683">Manage media and content settings</p>
      </div>

      <Card data-id="element-2684">
        <CardHeader data-id="element-2685">
          <CardTitle data-id="element-2686">Default Images</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-2687">
          <div data-id="element-2688">
            <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-2689">
              Placeholder Image for Options
            </label>
            <div className="flex items-center gap-4" data-id="element-2690">
              <div className="h-20 w-20 bg-slate-100 rounded-lg flex items-center justify-center border border-slate-200" data-id="element-2691">
                <Image className="h-8 w-8 text-slate-400" data-id="element-2692" />
              </div>
              <Button variant="outline" leftIcon={<Upload className="h-4 w-4" data-id="element-2694" />} data-id="element-2693">
                Upload Image
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card data-id="element-2695">
        <CardHeader data-id="element-2696">
          <CardTitle data-id="element-2697">Media Library</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-2698">
          <div className="grid grid-cols-2 gap-4" data-id="element-2699">
            <div className="p-4 bg-slate-50 rounded-lg" data-id="element-2700">
              <p className="text-2xl font-bold text-slate-900" data-id="element-2701">248</p>
              <p className="text-sm text-slate-500" data-id="element-2702">Total Images</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg" data-id="element-2703">
              <p className="text-2xl font-bold text-slate-900" data-id="element-2704">1.2 GB</p>
              <p className="text-sm text-slate-500" data-id="element-2705">Storage Used</p>
            </div>
          </div>
          <Button variant="outline" className="w-full" data-id="element-2706">
            Manage Media Library
          </Button>
        </CardContent>
      </Card>

      <Card data-id="element-2707">
        <CardHeader data-id="element-2708">
          <CardTitle data-id="element-2709">Upload Limits</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-2710">
          <Input label="Max File Size (MB)" defaultValue="10" type="number" onChange={onChange} data-id="element-2711" />
          <div data-id="element-2712">
            <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-2713">
              Allowed File Types
            </label>
            <div className="space-y-2" data-id="element-2714">
              {['JPG/JPEG', 'PNG', 'WebP', 'SVG', 'GIF'].map(type => <label key={type} className="flex items-center gap-2" data-id="element-2715">
                  <input type="checkbox" defaultChecked onChange={onChange} data-id="element-2716" />
                  <span className="text-sm text-slate-700" data-id="element-2717">{type}</span>
                </label>)}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card data-id="element-2718">
        <CardHeader data-id="element-2719">
          <CardTitle data-id="element-2720">Video Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-2721">
          <div className="flex items-center justify-between" data-id="element-2722">
            <div data-id="element-2723">
              <p className="font-medium text-slate-900" data-id="element-2724">Enable Video Tours</p>
              <p className="text-sm text-slate-500" data-id="element-2725">
                Allow video content in tours
              </p>
            </div>
            <Toggle defaultChecked onChange={onChange} data-id="element-2726" />
          </div>
          <div className="flex items-center justify-between" data-id="element-2727">
            <div data-id="element-2728">
              <p className="font-medium text-slate-900" data-id="element-2729">Autoplay Videos</p>
              <p className="text-sm text-slate-500" data-id="element-2730">
                Start videos automatically
              </p>
            </div>
            <Toggle onChange={onChange} data-id="element-2731" />
          </div>
        </CardContent>
      </Card>
    </div>;
}